/**
 * PURELY NUTRIENT — Product Page Template JS
 * File: assets/pn-product.js
 *
 * Covers:
 *  1. Countdown Timer
 *  2. Product Gallery (arrows + thumbnails + swipe)
 *  3. Bundle Selector
 *  4. Mini FAQ Accordion
 *  5. Comparison Bar Animation (IntersectionObserver)
 *  6. Ingredient Carousel (auto-advance + dots + swipe)
 *  7. Testimonials Carousel (auto-advance + dots + swipe)
 *  8. Full FAQ Accordion
 *  9. Sticky Buy Bar
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
   * 1. COUNTDOWN TIMER
   * ───────────────────────────────────────────── */
  function initCountdown() {
    var el = document.querySelector('.pn-countdown');
    if (!el) return;

    var minutes = parseInt(el.dataset.minutes, 10) || 120;
    var storageKey = 'pn_countdown_end';
    var endTime = parseInt(sessionStorage.getItem(storageKey), 10);

    if (!endTime || endTime < Date.now()) {
      endTime = Date.now() + minutes * 60 * 1000;
      sessionStorage.setItem(storageKey, endTime);
    }

    var hoursEl = el.querySelector('[data-hours]');
    var minsEl  = el.querySelector('[data-mins]');
    var secsEl  = el.querySelector('[data-secs]');

    function pad(n) { return String(n).padStart(2, '0'); }

    function tick() {
      var diff = Math.max(0, endTime - Date.now());
      var h = Math.floor(diff / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      if (hoursEl) hoursEl.textContent = pad(h);
      if (minsEl)  minsEl.textContent  = pad(m);
      if (secsEl)  secsEl.textContent  = pad(s);
      if (diff > 0) setTimeout(tick, 1000);
    }
    tick();
  }

  /* ─────────────────────────────────────────────
   * 2. PRODUCT GALLERY
   * ───────────────────────────────────────────── */
  function initGallery() {
    var gallery = document.getElementById('pn-gallery');
    if (!gallery) return;

    var slidesWrap = document.getElementById('pn-gallery-slides');
    var thumbsWrap = document.getElementById('pn-gallery-thumbs');
    if (!slidesWrap) return;

    var slides = Array.from(slidesWrap.querySelectorAll('.pn-gallery__slide'));
    var thumbs = thumbsWrap ? Array.from(thumbsWrap.querySelectorAll('.pn-gallery__thumb')) : [];
    var current = 0;

    function goTo(index) {
      current = (index + slides.length) % slides.length;
      slidesWrap.style.transform = 'translateX(-' + (current * 100) + '%)';
      thumbs.forEach(function (t, i) {
        t.classList.toggle('active', i === current);
      });
    }

    var prevBtn = gallery.querySelector('.pn-gallery__arrow--prev');
    var nextBtn = gallery.querySelector('.pn-gallery__arrow--next');
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1); });

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { goTo(i); });
      thumb.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goTo(i); }
      });
    });

    // Touch / swipe
    var touchStartX = 0;
    slidesWrap.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    slidesWrap.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 40) goTo(dx < 0 ? current + 1 : current - 1);
    }, { passive: true });

    goTo(0);
  }

  /* ─────────────────────────────────────────────
   * 3. BUNDLE SELECTOR
   * ───────────────────────────────────────────── */
  function initBundles() {
    var container = document.getElementById('pn-bundle-options');
    if (!container) return;

    var bundles = Array.from(container.querySelectorAll('.pn-bundle'));

    bundles.forEach(function (bundle) {
      bundle.addEventListener('click', function () {
        bundles.forEach(function (b) {
          b.classList.remove('selected');
          b.setAttribute('aria-checked', 'false');
        });
        bundle.classList.add('selected');
        bundle.setAttribute('aria-checked', 'true');
      });
      bundle.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); bundle.click(); }
      });
    });
  }

  /* ─────────────────────────────────────────────
   * 4. MINI FAQ ACCORDION
   * ───────────────────────────────────────────── */
  function initMiniAccordion() {
    var items = document.querySelectorAll('.pn-mini-faq__item');
    items.forEach(function (item) {
      var btn = item.querySelector('.pn-mini-faq__question');
      if (!btn) return;
      btn.addEventListener('click', function () {
        var isOpen = item.classList.contains('open');
        items.forEach(function (i) { i.classList.remove('open'); });
        if (!isOpen) item.classList.add('open');
        btn.setAttribute('aria-expanded', !isOpen);
      });
    });
  }

  /* ─────────────────────────────────────────────
   * 5. COMPARISON BAR ANIMATION
   * ───────────────────────────────────────────── */
  function initBars() {
    var bars = document.querySelectorAll('.pn-bar-item__fill');
    if (!bars.length) return;

    var animated = false;

    function animateBars() {
      if (animated) return;
      animated = true;
      bars.forEach(function (bar) {
        var target = bar.dataset.width || '0%';
        setTimeout(function () {
          bar.style.width = target;
        }, 100);
      });
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { animateBars(); observer.disconnect(); }
        });
      }, { threshold: 0.3 });
      var barsSection = document.getElementById('pn-bars');
      if (barsSection) observer.observe(barsSection);
    } else {
      animateBars();
    }
  }

  /* ─────────────────────────────────────────────
   * GENERIC CAROUSEL HELPER
   * ───────────────────────────────────────────── */
  function makeCarousel(options) {
    var slides    = options.slides;
    var dots      = options.dots || [];
    var perPage   = options.perPage || 1;
    var autoMs    = options.autoMs || 0;
    var onGo      = options.onGo || null;
    var current   = 0;
    var timer     = null;
    var total     = Math.ceil(slides.length / perPage);

    function goTo(index) {
      current = (index + total) % total;
      slides.forEach(function (s, i) {
        var pageIdx = Math.floor(i / perPage);
        s.classList.toggle('active', pageIdx === current);
      });
      dots.forEach(function (d, i) {
        d.classList.toggle('active', i === current);
      });
      if (onGo) onGo(current);
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        clearInterval(timer);
        goTo(i);
        if (autoMs) timer = setInterval(function () { goTo(current + 1); }, autoMs);
      });
    });

    if (autoMs) {
      timer = setInterval(function () { goTo(current + 1); }, autoMs);
    }

    // Touch swipe support
    function addSwipe(el) {
      var startX = 0;
      el.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
      }, { passive: true });
      el.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) {
          clearInterval(timer);
          goTo(dx < 0 ? current + 1 : current - 1);
          if (autoMs) timer = setInterval(function () { goTo(current + 1); }, autoMs);
        }
      }, { passive: true });
    }

    goTo(0);
    return { goTo: goTo, addSwipe: addSwipe };
  }

  /* ─────────────────────────────────────────────
   * 6. INGREDIENT CAROUSEL
   * ───────────────────────────────────────────── */
  function initIngredientCarousel() {
    var wrap   = document.getElementById('pn-ingredient-carousel');
    var dotsEl = document.getElementById('pn-ingredient-dots');
    if (!wrap) return;

    var slides = Array.from(wrap.querySelectorAll('.pn-ingredient-card'));
    var dots   = dotsEl ? Array.from(dotsEl.querySelectorAll('.pn-carousel-dot')) : [];

    var c = makeCarousel({ slides: slides, dots: dots, perPage: 1 });
    c.addSwipe(wrap);
  }

  /* ─────────────────────────────────────────────
   * 7. TESTIMONIALS CAROUSEL
   * ───────────────────────────────────────────── */
  function initTestimonialsCarousel() {
    var wrap   = document.getElementById('pn-testimonials-carousel');
    var dotsEl = document.getElementById('pn-testimonials-dots');
    if (!wrap) return;

    var slides = Array.from(wrap.querySelectorAll('.pn-testimonial-slide'));
    var dots   = dotsEl ? Array.from(dotsEl.querySelectorAll('.pn-carousel-dot')) : [];
    var perPage = window.innerWidth > 700 ? 2 : 1;

    var c = makeCarousel({ slides: slides, dots: dots, perPage: perPage});
    c.addSwipe(wrap);

    // Recalculate perPage on resize
    window.addEventListener('resize', function () {
      perPage = window.innerWidth > 700 ? 2 : 1;
    });
  }

  /* ─────────────────────────────────────────────
   * 8. FULL FAQ ACCORDION
   * ───────────────────────────────────────────── */
  function initFaqAccordion() {
    var items = document.querySelectorAll('.pn-faq__item');
    items.forEach(function (item) {
      var btn = item.querySelector('.pn-faq__question');
      if (!btn) return;
      btn.addEventListener('click', function () {
        var isOpen = item.classList.contains('open');
        items.forEach(function (i) { i.classList.remove('open'); });
        if (!isOpen) item.classList.add('open');
        btn.setAttribute('aria-expanded', !isOpen);
      });
    });
  }

  /* ─────────────────────────────────────────────
   * 9. STICKY BUY BAR
   * ───────────────────────────────────────────── */
  function initStickyBar() {
    var bar = document.querySelector('.pn-sticky-bar');
    if (!bar) return;

    var hero = document.getElementById('pn-hero');
    if (!hero) return;

    var btn = bar.querySelector('.pn-sticky-bar__btn');
    if (btn) {
      btn.addEventListener('click', function () {
        var atcBtn = document.getElementById('pn-btn-atc');
        if (atcBtn) {
          atcBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(function () { atcBtn.click(); }, 600);
        }
      });
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('visible', !entry.isIntersecting);
      });
    }, { threshold: 0 });
    observer.observe(hero);
  }

  /* ─────────────────────────────────────────────
   * INIT ALL
   * ───────────────────────────────────────────── */
  function init() {
    initCountdown();
    initGallery();
    initBundles();
    initMiniAccordion();
    initBars();
    initIngredientCarousel();
    initTestimonialsCarousel();
    initFaqAccordion();
    initStickyBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
